import * as apiEvents from "./api-events";
import type { components } from "../types/api.gen";
import type {
  AdminPlayerSummary,
  AdminUpdatePlayerPayload,
  AdminTournamentDetail,
  AuthUser,
  DashboardPayload,
  GameDetail,
  GameSummary,
  HandCreateInput,
  LeaderboardEntry,
  LeaderboardFilter,
  MagicLinkRequest,
  MagicLinkResponse,
  QuickGameCreateInput,
  QuickRuleset,
  TournamentDetail,
  TournamentSummary,
  TournamentCreateInput,
  TournamentTeamCreateInput,
  UserNotification,
} from "../types/domain";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

type ScanSide = "left" | "right";
export type CardScanInput = {
  requestId: string;
  handId: string | null;
  side: ScanSide;
  lastTrickSide: ScanSide;
  trump: string;
  image: string;
};

export type CardScanResult =
  | {
      status: "accepted";
      requestId: string;
      side: ScanSide;
      lastTrickSide: ScanSide;
      trump: string;
      cardCount: number;
      pointsLeft: number;
      pointsRight: number;
      pipelineVersion: string;
    }
  | {
      status: "retake";
      requestId: string;
      reason: string;
      cardCount: number | null;
      pointsLeft: null;
      pointsRight: null;
    }
  | {
      status: "manual_required";
      requestId: string;
      reason: string;
      pointsLeft: null;
      pointsRight: null;
    };

export class ScanApiError extends Error {
  constructor(
    public status: number,
    public reason: string,
    public retryAt: string | null = null,
  ) {
    super(reason);
  }
}

function validScanResponse(
  value: unknown,
  input: CardScanInput,
): value is CardScanResult {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (v.requestId !== input.requestId) return false;
  if (v.status === "accepted") {
    return (
      v.side === input.side &&
      v.lastTrickSide === input.lastTrickSide &&
      v.trump === input.trump &&
      typeof v.cardCount === "number" &&
      Number.isInteger(v.cardCount) &&
      v.cardCount >= 4 &&
      v.cardCount <= 16 &&
      v.cardCount % 4 === 0 &&
      typeof v.pointsLeft === "number" &&
      typeof v.pointsRight === "number" &&
      Number.isInteger(v.pointsLeft) &&
      Number.isInteger(v.pointsRight) &&
      v.pointsLeft > 0 &&
      v.pointsRight > 0 &&
      v.pointsLeft + v.pointsRight === 162 &&
      typeof v.pipelineVersion === "string"
    );
  }
  return (
    (v.status === "retake" || v.status === "manual_required") &&
    typeof v.reason === "string" &&
    v.pointsLeft === null &&
    v.pointsRight === null
  );
}

// Scanner errors stay local to its overlay; the global API toast would duplicate them.
export async function analyzeCardScan(
  gameId: string,
  input: CardScanInput,
  signal: AbortSignal,
): Promise<CardScanResult> {
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE_URL}/api/games/${encodeURIComponent(gameId)}/card-scans/analyze`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
        signal,
      },
    );
  } catch (error) {
    if (signal.aborted) throw error;
    throw new ScanApiError(503, "network");
  }
  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new ScanApiError(response.status, "unavailable");
  }
  if (!response.ok) {
    const scan = (
      raw as { scan?: { reason?: unknown; retryAt?: unknown } } | null
    )?.scan;
    throw new ScanApiError(
      response.status,
      typeof scan?.reason === "string" ? scan.reason : "unavailable",
      typeof scan?.retryAt === "string" ? scan.retryAt : null,
    );
  }
  if (!validScanResponse(raw, input))
    throw new ScanApiError(503, "invalid_response");
  return raw;
}

export type CardValues = components["schemas"]["CardValues"];

/** The API flag is the single switch for the scanner; any failure keeps it hidden. */
export async function fetchCardScanEnabled(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/features`, {
      credentials: "omit",
    });
    if (!response.ok) return false;
    const features = (await response.json()) as Partial<
      components["schemas"]["Features"]
    >;
    return features.cardScan === true;
  } catch {
    return false;
  }
}

type BackendGameSummary = components["schemas"]["GameSummary"];
type BackendGameDetail = components["schemas"]["GameDetail"];
type BackendHandSummary = components["schemas"]["HandSummary"];
type BackendGameSide = components["schemas"]["GameSide"];
type BackendLeaderboardEntry = components["schemas"]["LeaderboardEntry"];
type BackendTournamentSummary = components["schemas"]["TournamentSummary"];
type BackendTournamentDetail = components["schemas"]["TournamentDetail"];
type BackendTournamentStanding = components["schemas"]["TournamentStanding"];
type BackendAdminTournamentDetail =
  components["schemas"]["AdminTournamentDetail"];
type BackendDashboardPayload = components["schemas"]["DashboardPayload"];
type BackendPlayerReference = components["schemas"]["PlayerReference"];
export type BackendFinalsMatch = components["schemas"]["FinalsMatch"];
type BackendScorePreviewResponse =
  components["schemas"]["ScorePreviewResponse"];

type BackendAdminPlayerSummary = components["schemas"]["AdminPlayerSummary"];

type BackendScope = components["schemas"]["LeaderboardScope"];
type BackendTournamentStatus = components["schemas"]["TournamentStatus"];
type BackendFinalsFormat = components["schemas"]["FinalsFormat"];
type BackendTrump = components["schemas"]["Trump"];
type BackendContractFamily = components["schemas"]["ContractFamily"];
type BackendCoincheState = components["schemas"]["CoincheState"];

let _currentUserId: string | undefined;

class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = response.statusText;

    try {
      const payload = (await response.json()) as {
        detail?: string | Array<{ msg: string }>;
        error?: string;
      };
      const detail = payload.detail;
      if (Array.isArray(detail)) {
        message = detail.map((e) => e.msg).join(", ");
      } else {
        message = detail ?? payload.error ?? message;
      }
    } catch {
      // Ignore JSON parsing errors for non-JSON responses.
    }

    throw new ApiError(message, response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  apiEvents.requestStarted();
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
      ...init,
    });

    return await parseResponse<T>(response);
  } catch (error) {
    const apiError =
      error instanceof ApiError
        ? error
        : new ApiError("L'API leboncoinche est indisponible.", 503);

    // Don't notify on 401 (handled by auth context)
    if (apiError.status !== 401) {
      apiEvents.notify("error", apiError.message);
    }

    throw apiError;
  } finally {
    apiEvents.requestEnded();
  }
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function scopeLabel(scope: BackendScope): string {
  if (scope === "quick") {
    return "Rapides";
  }
  if (scope === "tournament") {
    return "Tournoi";
  }
  return "Tous";
}

function sideName(side: BackendGameSide, fallback: string): string {
  if (side.teamName) {
    return side.teamName;
  }
  const pseudos = (side.players ?? [])
    .map((player) => player.pseudo)
    .filter(Boolean);
  if (pseudos.length > 0) {
    return pseudos.join(" / ");
  }
  return fallback;
}

function gameTitle(game: BackendGameSummary, currentUserId?: string): string {
  let leftSide =
    (game.sides ?? []).find((s) => s.key === "left") ?? (game.sides ?? [])[0];
  let rightSide =
    (game.sides ?? []).find((s) => s.key === "right") ?? (game.sides ?? [])[1];
  let scoreLeft = game.scoreLeft;
  let scoreRight = game.scoreRight;

  // Put current user's side on the left
  if (
    currentUserId &&
    (rightSide?.players ?? []).some((p) => p.id === currentUserId)
  ) {
    [leftSide, rightSide] = [rightSide, leftSide];
    [scoreLeft, scoreRight] = [scoreRight, scoreLeft];
  }

  const isFinished = game.status === "validated";
  const leftWon = isFinished && scoreLeft > scoreRight;
  const rightWon = isFinished && scoreRight > scoreLeft;

  const left = sideName(
    leftSide ?? { players: [], key: "left", teamId: null, teamName: null },
    "Équipe 1",
  );
  const right = sideName(
    rightSide ?? { players: [], key: "right", teamId: null, teamName: null },
    "Équipe 2",
  );
  return `${leftWon ? "🏆 " : ""}${left} vs ${rightWon ? "🏆 " : ""}${right}`;
}

function gameSubtitle(game: BackendGameSummary): string {
  return formatDate(game.createdAt);
}

function gameScoreLabels(game: BackendGameSummary): [string, string] {
  if (game.mode === "quick") {
    return ["Nous", "Eux"];
  }
  const sides = game.sides ?? [];
  return [
    sideName(
      sides[0] ?? {
        players: [],
        key: "left",
        teamId: null,
        teamName: null,
      },
      "Équipe 1",
    ),
    sideName(
      sides[1] ?? {
        players: [],
        key: "right",
        teamId: null,
        teamName: null,
      },
      "Équipe 2",
    ),
  ];
}

function formatTrump(trump: BackendTrump): string {
  switch (trump) {
    case "spades":
      return "♠";
    case "hearts":
      return "♥";
    case "diamonds":
      return "♦";
    case "clubs":
      return "♣";
    case "sans_atout":
      return "SA";
    case "tout_atout":
      return "TA";
  }
}

function contractLabel(hand: BackendHandSummary): string {
  switch (hand.contractFamily) {
    case "couleur":
      return `${hand.contractValue ?? 0} ${formatTrump(hand.trump)}`;
    case "sans_atout":
      return `${hand.contractValue ?? 0} SA`;
    case "tout_atout":
      return `${hand.contractValue ?? 0} TA`;
    case "capot":
      return `Capot ${formatTrump(hand.trump)}`;
    case "generale":
      return `Générale ${formatTrump(hand.trump)}`;
  }
}

function coincheLabel(state: BackendCoincheState): string {
  if (state === "surcoinche") {
    return "Surcoinche";
  }
  if (state === "coinche") {
    return "Coinche";
  }
  return "Simple";
}

function finalsSlots(finalsFormat: BackendFinalsFormat): string[] {
  if (finalsFormat === "top_4") {
    return ["1 vs 4", "2 vs 3", "Finale", "Petite finale"];
  }
  return [];
}

function ruleHighlights(tournament: BackendTournamentSummary): string[] {
  return [
    `${tournament.quotaHands} levées`,
    tournament.ruleset.surcoincheEnabled ? "Surcoinche" : "Coinche",
    tournament.ruleset.blankGamesEnabled
      ? "Parties à blanc autorisées"
      : "Parties à blanc désactivées",
  ];
}

function mapGameSummary(
  game: BackendGameSummary,
  currentUserId: string | undefined = _currentUserId,
): GameSummary {
  const sides = game.sides ?? [];
  let left = sides.find((s) => s.key === "left");
  let right = sides.find((s) => s.key === "right");
  const userOnRight =
    currentUserId && (right?.players ?? []).some((p) => p.id === currentUserId);
  if (userOnRight) {
    [left, right] = [right, left];
  }
  return {
    id: game.id,
    mode: game.mode,
    type: game.type,
    title: gameTitle(game, currentUserId),
    subtitle: gameSubtitle(game),
    status: game.status ?? null,
    scoreLeft: userOnRight ? game.scoreRight : game.scoreLeft,
    scoreRight: userOnRight ? game.scoreLeft : game.scoreRight,
    createdAt: game.createdAt,
    leftPlayers: (left?.players ?? []).map((p) => p.pseudo).join(", ") ?? "",
    rightPlayers: (right?.players ?? []).map((p) => p.pseudo).join(", ") ?? "",
    tournamentId: game.tournamentId ?? null,
    tournamentName: game.tournamentName ?? null,
    handsCount: game.handsCount ?? 0,
  };
}

function mapGameDetail(game: BackendGameDetail): GameDetail {
  // Determine contextual labels based on which side the current user is on
  const swapped = game.mySide === "right";
  const nousLabel = "Nous";
  const euxLabel = "Eux";

  function takerLabel(takerSide: string): string {
    if (swapped) {
      return takerSide === "right" ? nousLabel : euxLabel;
    }
    return takerSide === "left" ? nousLabel : euxLabel;
  }

  const labels: [string, string] = swapped
    ? [euxLabel, nousLabel]
    : gameScoreLabels(game);

  return {
    ...mapGameSummary(game),
    targetScore: game.targetScore ?? null,
    scoreLabels: labels,
    quotaLabels: gameScoreLabels(game),
    supportScoreIgnored: game.supportScoreIgnored,
    hands: (game.hands ?? []).map((hand) => ({
      id: hand.id,
      orderIndex: hand.orderIndex,
      takerLabel: `${takerLabel(hand.takerSide)} preneurs • ${contractLabel(hand)}`,
      contract: contractLabel(hand),
      leftScore: swapped ? hand.rightScore : hand.leftScore,
      rightScore: swapped ? hand.leftScore : hand.rightScore,
      takerSide: swapped
        ? hand.takerSide === "left"
          ? "right"
          : "left"
        : hand.takerSide,
      contractFamily: hand.contractFamily,
      contractValue: hand.contractValue ?? null,
      trump: hand.trump,
      pointsLeft: swapped ? hand.pointsRight : hand.pointsLeft,
      pointsRight: swapped ? hand.pointsLeft : hand.pointsRight,
      beloteLeft: swapped ? hand.beloteRight : hand.beloteLeft,
      beloteRight: swapped ? hand.beloteLeft : hand.beloteRight,
      generaleMade: hand.generaleMade,
      coincheState: hand.coincheState,
      foulSide:
        swapped && hand.foulSide
          ? hand.foulSide === "left"
            ? "right"
            : "left"
          : ((hand.foulSide as "left" | "right" | null) ?? null),
      success: hand.success,
    })),
    submittedBySide: game.submittedBySide ?? null,
    mySide: game.mySide ?? null,
    handsEditable: game.handsEditable ?? false,
    isCreator: game.isCreator ?? false,
    isParticipant: game.isParticipant ?? false,
    quotaHands: game.quotaHands ?? null,
    countedHandsBeforeLeft: game.countedHandsBeforeLeft ?? null,
    countedHandsBeforeRight: game.countedHandsBeforeRight ?? null,
    ruleset: {
      beloteEnabled: game.ruleset?.beloteEnabled ?? true,
      sansAtoutEnabled: game.ruleset?.sansAtoutEnabled ?? true,
      toutAtoutEnabled: game.ruleset?.toutAtoutEnabled ?? true,
      surcoincheEnabled: game.ruleset?.surcoincheEnabled ?? true,
      foulEnabled: game.ruleset?.foulEnabled ?? true,
    },
  };
}

function mapTournamentSummary(
  tournament: BackendTournamentSummary,
): TournamentSummary {
  return {
    id: tournament.id,
    name: tournament.name,
    description: tournament.description,
    status: tournament.status,
    teamCount: tournament.teamCount,
    quotaHands: tournament.quotaHands,
    topN: tournament.topN,
    supportBlankLimit: tournament.supportBlankLimit,
    ruleset: tournament.ruleset,
    ruleHighlights: ruleHighlights(tournament),
    registrationStart: tournament.registrationStart ?? null,
    registrationEnd: tournament.registrationEnd ?? null,
    myTeamId: tournament.myTeamId ?? null,
  };
}

function mapTournamentStanding(
  row: BackendTournamentStanding,
): import("../types/domain").TournamentStanding {
  return {
    rank: row.rank,
    teamId: row.teamId,
    teamName: row.teamName,
    points: row.points,
    countedHands: row.countedHands,
    quota: row.quotaHands,
    status: row.status,
    qualifiesForFinals: row.qualifiesForFinals,
    blankSupportHands: row.blankSupportHands,
    playerOnePseudo: row.playerOnePseudo ?? null,
    playerTwoPseudo: row.playerTwoPseudo ?? null,
  };
}

function mapTournamentDetail(
  tournament: BackendTournamentDetail,
): TournamentDetail {
  return {
    ...mapTournamentSummary(tournament),
    standingRows: (tournament.standingRows ?? []).map(mapTournamentStanding),
    recentGames: (tournament.recentGames ?? []).map((g) => mapGameSummary(g)),
    finalsSlots: finalsSlots(tournament.finalsFormat),
  };
}

function mapAdminTournament(
  payload: BackendAdminTournamentDetail,
): AdminTournamentDetail {
  return {
    tournament: mapTournamentDetail(payload.tournament),
    metrics: [
      { label: "Joueurs", value: payload.metrics.registrations },
      { label: "Équipes", value: payload.metrics.teams },
      { label: "Parties à valider", value: payload.metrics.pendingGames },
    ],
    pendingGames: (payload.pendingGames ?? []).map((g) => mapGameSummary(g)),
  };
}

function mapDashboard(payload: BackendDashboardPayload): DashboardPayload {
  return {
    me: { ...payload.me, isAdmin: payload.me.role === "admin" },
    tournaments: (payload.tournaments ?? []).map(mapTournamentSummary),
    leaderboard: (payload.leaderboard ?? []).map((entry) => ({
      playerId: entry.playerId,
      pseudo: entry.pseudo,
      scopeLabel: scopeLabel(entry.scope),
      score: entry.score ?? 0,
      differential: entry.differential,
      gamesPlayed: entry.gamesPlayed ?? 0,
    })),
  };
}

interface PlayerOption {
  id: string;
  pseudo: string;
}

export const api = {
  async getCardValues(): Promise<CardValues> {
    return request<CardValues>("/api/scoring/card-values", {
      credentials: "omit",
    });
  },

  async listPlayers(): Promise<PlayerOption[]> {
    try {
      return await request<PlayerOption[]>("/api/players");
    } catch {
      return [];
    }
  },

  async listAdminPlayers(): Promise<AdminPlayerSummary[]> {
    try {
      return await request<BackendAdminPlayerSummary[]>("/api/admin/players");
    } catch {
      return [];
    }
  },

  async getAdminPlayerDefaults(): Promise<
    components["schemas"]["AdminPlayerDefaults"]
  > {
    return await request("/api/admin/players/defaults");
  },

  async adminCreatePlayer(
    payload: components["schemas"]["AdminCreatePlayerRequest"],
  ): Promise<AdminPlayerSummary> {
    return await request<BackendAdminPlayerSummary>("/api/admin/players", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async adminUpdatePlayer(
    playerId: string,
    payload: AdminUpdatePlayerPayload,
  ): Promise<AdminPlayerSummary> {
    return await request<AdminPlayerSummary>(`/api/admin/players/${playerId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  },

  async getMe(): Promise<AuthUser | null> {
    try {
      const user = await request<AuthUser>("/api/me");
      _currentUserId = user.id;
      user.isAdmin = user.role === "admin";
      return user;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        return null;
      }

      throw error;
    }
  },

  async requestMagicLink(
    payload: MagicLinkRequest,
  ): Promise<MagicLinkResponse> {
    return await request<MagicLinkResponse>("/api/auth/request-link", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async consumeMagicLink(token: string): Promise<AuthUser> {
    const user = await request<AuthUser>(
      `/api/auth/consume-link?token=${encodeURIComponent(token)}`,
    );
    user.isAdmin = user.role === "admin";
    return user;
  },

  async updateProfile(pseudo: string): Promise<AuthUser> {
    const user = await request<AuthUser>("/api/profile", {
      method: "PATCH",
      body: JSON.stringify({ pseudo }),
    });
    user.isAdmin = user.role === "admin";
    return user;
  },

  async logout(): Promise<void> {
    try {
      await request<void>("/api/auth/logout", { method: "POST" });
    } catch {
      // Local fallback.
    }
  },

  async getDashboard(): Promise<DashboardPayload> {
    try {
      const payload = await request<BackendDashboardPayload>("/api/dashboard");
      return mapDashboard(payload);
    } catch {
      return {
        me: {
          id: "",
          email: "",
          pseudo: null,
          role: "player" as const,
          isAdmin: false,
          status: "pending_profile" as const,
          hasCompletedProfile: false,
        },
        tournaments: [],
        leaderboard: [],
      };
    }
  },

  async listGames(): Promise<GameSummary[]> {
    try {
      const games = await request<BackendGameSummary[]>("/api/games");
      return games.map((g) => mapGameSummary(g));
    } catch {
      return [];
    }
  },

  async listMyGames(): Promise<GameSummary[]> {
    try {
      const games = await request<BackendGameSummary[]>(
        "/api/games?scope=mine",
      );
      return games.map((g) => mapGameSummary(g));
    } catch {
      return [];
    }
  },

  async createQuickGame(payload: QuickGameCreateInput): Promise<GameDetail> {
    const game = await request<BackendGameDetail>("/api/games/quick", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return mapGameDetail(game);
  },

  async getGame(gameId: string): Promise<GameDetail> {
    const game = await request<BackendGameDetail>(`/api/games/${gameId}`);
    return mapGameDetail(game);
  },

  async addHand(gameId: string, payload: HandCreateInput): Promise<GameDetail> {
    const game = await request<BackendGameDetail>(
      `/api/games/${gameId}/hands`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
    return mapGameDetail(game);
  },

  async updateHand(
    gameId: string,
    handId: string,
    payload: HandCreateInput,
  ): Promise<GameDetail> {
    const game = await request<BackendGameDetail>(
      `/api/games/${gameId}/hands/${handId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
    );
    return mapGameDetail(game);
  },

  async deleteHand(gameId: string, handId: string): Promise<GameDetail> {
    const game = await request<BackendGameDetail>(
      `/api/games/${gameId}/hands/${handId}`,
      { method: "DELETE" },
    );
    return mapGameDetail(game);
  },

  async previewScore(
    payload: HandCreateInput,
  ): Promise<BackendScorePreviewResponse | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/games/preview-score`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) return null;
      return (await response.json()) as BackendScorePreviewResponse;
    } catch {
      return null;
    }
  },

  async submitGame(gameId: string): Promise<GameDetail> {
    const game = await request<BackendGameDetail>(
      `/api/games/${gameId}/submit`,
      { method: "POST" },
    );
    return mapGameDetail(game);
  },

  async validateGame(gameId: string): Promise<GameDetail> {
    const game = await request<BackendGameDetail>(
      `/api/games/${gameId}/validate`,
      { method: "POST" },
    );
    return mapGameDetail(game);
  },

  async disputeGame(gameId: string): Promise<GameDetail> {
    const game = await request<BackendGameDetail>(
      `/api/games/${gameId}/dispute`,
      { method: "POST" },
    );
    return mapGameDetail(game);
  },

  async updateGame(
    gameId: string,
    payload: { targetScore: number },
  ): Promise<GameDetail> {
    const game = await request<BackendGameDetail>(`/api/games/${gameId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    return mapGameDetail(game);
  },

  async deleteGame(gameId: string): Promise<void> {
    await request<void>(`/api/games/${gameId}`, { method: "DELETE" });
  },

  async createTournamentGame(payload: {
    tournamentId: string;
    opponentTeamId: string;
    blank?: boolean;
  }): Promise<GameDetail> {
    const game = await request<BackendGameDetail>("/api/games/tournament", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return mapGameDetail(game);
  },

  async listTournaments(): Promise<TournamentSummary[]> {
    try {
      const tournaments =
        await request<BackendTournamentSummary[]>("/api/tournaments");
      return tournaments.map(mapTournamentSummary);
    } catch {
      return [];
    }
  },

  async listFinals(tournamentId: string): Promise<BackendFinalsMatch[]> {
    try {
      return await request<BackendFinalsMatch[]>(
        `/api/tournaments/${tournamentId}/finals`,
      );
    } catch {
      return [];
    }
  },

  async generateFinals(tournamentId: string): Promise<BackendFinalsMatch[]> {
    return await request<BackendFinalsMatch[]>(
      `/api/admin/tournaments/${tournamentId}/finals/generate`,
      { method: "POST" },
    );
  },

  async forceValidateGame(
    tournamentId: string,
    gameId: string,
  ): Promise<AdminTournamentDetail> {
    const result = await request<BackendAdminTournamentDetail>(
      `/api/admin/tournaments/${tournamentId}/games/${gameId}/force-validate`,
      { method: "POST" },
    );
    return mapAdminTournament(result);
  },

  async createFinalsGame(
    tournamentId: string,
    matchId: string,
  ): Promise<BackendFinalsMatch[]> {
    return await request<BackendFinalsMatch[]>(
      `/api/admin/tournaments/${tournamentId}/finals/${matchId}/create-game`,
      { method: "POST" },
    );
  },

  async listAvailablePlayers(tournamentId: string): Promise<PlayerOption[]> {
    try {
      return await request<PlayerOption[]>(
        `/api/tournaments/${tournamentId}/available-players`,
      );
    } catch {
      return [];
    }
  },

  async getTournament(tournamentId: string): Promise<TournamentDetail> {
    const tournament = await request<BackendTournamentDetail>(
      `/api/tournaments/${tournamentId}`,
    );
    return mapTournamentDetail(tournament);
  },

  async createTournamentTeam(
    tournamentId: string,
    payload: TournamentTeamCreateInput,
  ): Promise<TournamentDetail> {
    const tournament = await request<BackendTournamentDetail>(
      `/api/tournaments/${tournamentId}/teams`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
    return mapTournamentDetail(tournament);
  },

  async listLeaderboard(
    filter: LeaderboardFilter,
  ): Promise<LeaderboardEntry[]> {
    try {
      const entries = await request<BackendLeaderboardEntry[]>(
        `/api/leaderboard?filter=${encodeURIComponent(filter)}`,
      );
      return entries.map((entry) => ({
        playerId: entry.playerId,
        pseudo: entry.pseudo,
        scopeLabel: scopeLabel(entry.scope),
        score: entry.score ?? 0,
        differential: entry.differential,
        gamesPlayed: entry.gamesPlayed ?? 0,
      }));
    } catch {
      return [];
    }
  },

  async createTournament(
    payload: TournamentCreateInput,
  ): Promise<AdminTournamentDetail> {
    const result = await request<BackendAdminTournamentDetail>(
      "/api/admin/tournaments",
      { method: "POST", body: JSON.stringify(payload) },
    );
    return mapAdminTournament(result);
  },

  async updateTournament(
    tournamentId: string,
    payload: Partial<TournamentCreateInput>,
  ): Promise<AdminTournamentDetail> {
    const result = await request<BackendAdminTournamentDetail>(
      `/api/admin/tournaments/${tournamentId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
    );
    return mapAdminTournament(result);
  },

  async transitionTournamentStatus(
    tournamentId: string,
    targetStatus: string,
  ): Promise<AdminTournamentDetail> {
    const result = await request<BackendAdminTournamentDetail>(
      `/api/admin/tournaments/${tournamentId}/status`,
      { method: "POST", body: JSON.stringify({ targetStatus }) },
    );
    return mapAdminTournament(result);
  },

  async dissolveTeam(
    tournamentId: string,
    teamId: string,
  ): Promise<AdminTournamentDetail> {
    const result = await request<BackendAdminTournamentDetail>(
      `/api/admin/tournaments/${tournamentId}/teams/${teamId}/dissolve`,
      { method: "POST" },
    );
    return mapAdminTournament(result);
  },

  async adminCreateTeam(
    tournamentId: string,
    payload: {
      teamName: string;
      playerOnePseudo: string;
      playerTwoPseudo: string;
    },
  ): Promise<AdminTournamentDetail> {
    const result = await request<BackendAdminTournamentDetail>(
      `/api/admin/tournaments/${tournamentId}/teams`,
      { method: "POST", body: JSON.stringify(payload) },
    );
    return mapAdminTournament(result);
  },

  async reactivateTeam(
    tournamentId: string,
    teamId: string,
  ): Promise<AdminTournamentDetail> {
    const result = await request<BackendAdminTournamentDetail>(
      `/api/admin/tournaments/${tournamentId}/teams/${teamId}/reactivate`,
      { method: "POST" },
    );
    return mapAdminTournament(result);
  },

  async cancelTournamentGame(
    tournamentId: string,
    gameId: string,
  ): Promise<AdminTournamentDetail> {
    const result = await request<BackendAdminTournamentDetail>(
      `/api/admin/tournaments/${tournamentId}/games/${gameId}/cancel`,
      { method: "POST" },
    );
    return mapAdminTournament(result);
  },

  async recomputeTournamentScores(
    tournamentId: string,
  ): Promise<AdminTournamentDetail> {
    const result = await request<BackendAdminTournamentDetail>(
      `/api/admin/tournaments/${tournamentId}/recompute-scores`,
      { method: "POST" },
    );
    return mapAdminTournament(result);
  },

  async getAdminTournament(
    tournamentId: string,
  ): Promise<AdminTournamentDetail> {
    const payload = await request<BackendAdminTournamentDetail>(
      `/api/admin/tournaments/${tournamentId}`,
    );
    return mapAdminTournament(payload);
  },

  async getUnreadCount(): Promise<{ count: number }> {
    return request<{ count: number }>("/api/notifications/unread-count");
  },

  async getNotifications(): Promise<UserNotification[]> {
    return request<UserNotification[]>("/api/notifications");
  },

  async markNotificationRead(id: string): Promise<void> {
    await request<{ status: string }>(`/api/notifications/${id}/read`, {
      method: "POST",
    });
  },

  async markAllNotificationsRead(): Promise<void> {
    await request<{ status: string }>("/api/notifications/read-all", {
      method: "POST",
    });
  },
};

export { ApiError };
export { notify } from "./api-events";
